import { combineReducers, configureStore, createSlice } from '@reduxjs/toolkit';
import riskmizeReducer, { riskmizeActions } from './slices/riskmizeSlice.js';

// ═══ Session Slice ═══
const sessionSlice = createSlice({
  name: 'session',
  initialState: {
    user: null,
    server: {},
    positions: {},   // keyed by deviceId
    socket: false,
    expiry: null,    // { expiresAt, enforcementOverride, effectiveMode } from /api/auth/session
    modules: [],     // Array<{ slug, name, expiresAt, effectiveMode }> from /api/auth/session
    isHub: false,    // is this deployment the multi-server directory hub? (gates Servers/Backups nav)
    reseller: null,  // { id, name, status, limits, usage, expiresAt } from /api/auth/session (migration 063)
    platformBasemaps: [],  // [{id, name, styleUrl}] from /api/auth/session
    instance: null,  // { role: 'admin'|'support', capabilities: string[] } from /api/auth/session (migration 079)
    // Enterprise view: which kind of scope the current token carries. Set ONLY
    // by enterScope (via applySession in features/enterprise/sessionBootstrap.js).
    mode: 'normal',        // 'normal' | 'enterprise' | 'impersonated'
    returnMode: 'normal',  // where leaving impersonation lands: 'normal' | 'enterprise'
    enterprise: null,      // { id, name } of the reseller while mode === 'enterprise'
    // Scope generation: bumped by every enterScope. A tenant write stamped with
    // an older generation (withScope) is dropped by the root reducer.
    scopeGen: 0,
  },
  reducers: {
    // Shallow merge — partial updates (profile save, channel updates) must not
    // drop the derived `administrator` shim set by App.jsx + LoginPage. Full
    // replacement still works because state.user is null on first login.
    updateUser: (state, action) => { state.user = { ...(state.user || {}), ...action.payload }; },
    clearUser: (state) => {
      state.user = null; state.expiry = null; state.modules = []; state.reseller = null; state.platformBasemaps = []; state.instance = null;
      state.mode = 'normal'; state.returnMode = 'normal'; state.enterprise = null;
    },
    // ONE action per scope switch (login, reload, enterprise enter/exit, Manage,
    // impersonation exit). The root reducer empties every tenant slice on it.
    enterScope: (state, action) => {
      const { mode, returnMode, enterprise } = action.payload || {};
      state.mode = mode === 'enterprise' || mode === 'impersonated' ? mode : 'normal';
      state.returnMode = returnMode === 'enterprise' ? 'enterprise' : 'normal';
      state.enterprise = state.mode === 'enterprise' ? (enterprise ?? null) : null;
      state.scopeGen += 1;
      state.positions = {};
    },
    updateServer: (state, action) => { state.server = action.payload; },
    updateSocket: (state, action) => { state.socket = action.payload; },
    updateExpiry: (state, action) => { state.expiry = action.payload; },
    updateModules: (state, action) => { state.modules = action.payload ?? []; },
    updatePlatformBasemaps: (state, action) => { state.platformBasemaps = action.payload ?? []; },
    updateIsHub: (state, action) => { state.isHub = Boolean(action.payload); },
    updateReseller(state, action) {
      state.reseller = action.payload;
    },
    updateInstance(state, action) {
      state.instance = action.payload ?? null;
    },
    updatePositions: (state, action) => {
      // Positions come as array, index by deviceId for O(1) lookup
      const positions = action.payload;
      (Array.isArray(positions) ? positions : [positions]).forEach((pos) => {
        state.positions[pos.deviceId] = pos;
      });
    },
    // Full replacement (enterprise polling): devices that left the scope go away.
    replacePositions: (state, action) => {
      const next = {};
      (action.payload || []).forEach((pos) => { next[pos.deviceId] = pos; });
      state.positions = next;
    },
  },
});

// ═══ Devices Slice ═══
const devicesSlice = createSlice({
  name: 'devices',
  initialState: {
    items: {},       // keyed by id
    selectedId: null,
  },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((device) => {
        state.items[device.id] = device;
      });
    },
    update: (state, action) => {
      (Array.isArray(action.payload) ? action.payload : [action.payload]).forEach((device) => {
        state.items[device.id] = device;
      });
    },
    remove: (state, action) => { delete state.items[action.payload]; },
    selectId: (state, action) => { state.selectedId = action.payload; },
  },
});

// ═══ Staff Slice (Phase 3 — staff phones from staff_positions) ═══
// Mirrors useStaffPositions output into Redux so TelemetryPanel can resolve a
// selected staff person without lifting state up. byUserId stays in sync with
// the latest poll (10s); selectedUserId is mutually exclusive with
// devices.selectedId — selecting a staff clears the vehicle selection and
// vice versa (handled at the dispatch site, not in the reducer).
const staffSlice = createSlice({
  name: 'staff',
  initialState: {
    byUserId: {},          // keyed by userId, each row is a StaffLatestPosition
    selectedUserId: null,
  },
  reducers: {
    replaceAll: (state, action) => {
      const next = {};
      action.payload.forEach((row) => { next[row.userId] = row; });
      state.byUserId = next;
    },
    selectUser: (state, action) => { state.selectedUserId = action.payload; },
    clearSelection: (state) => { state.selectedUserId = null; },
  },
});

// ═══ Vehicles Slice (multi-tracker cars — migration 065) ═══
// Holds vehicle metadata (label/plate/...) + member trackers, keyed by id.
// Devices carry their own vehicleId/isPrimary (from /api/devices); this slice
// supplies the stable car LABEL so the list row survives a primary switch.
// Populated by the bootstrap in socket.js and refreshed after drawer edits.
const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState: { items: {} },   // keyed by vehicle id
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      (action.payload || []).forEach((v) => { state.items[v.id] = v; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Events Slice ═══
const eventsSlice = createSlice({
  name: 'events',
  initialState: {
    items: [],
  },
  reducers: {
    add: (state, action) => {
      state.items = [...action.payload, ...state.items].slice(0, 200); // keep last 200
    },
    clear: (state) => { state.items = []; },
  },
});

// ═══ Notifications Slice (in-app notifications) ═══
const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],       // last 50 notifications, newest first
    unreadCount: 0,
  },
  reducers: {
    add: (state, action) => {
      const notif = {
        ...action.payload,
        _id: Date.now() + Math.random(),
        _read: false,
        _timestamp: Date.now(),
      };
      state.items = [notif, ...state.items].slice(0, 50);
      state.unreadCount += 1;
    },
    markRead: (state, action) => {
      const item = state.items.find((n) => n._id === action.payload);
      if (item && !item._read) {
        item._read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllRead: (state) => {
      state.items.forEach((n) => { n._read = true; });
      state.unreadCount = 0;
    },
    clear: (state) => {
      state.items = [];
      state.unreadCount = 0;
    },
  },
});

// ═══ Geofences Slice ═══
const geofencesSlice = createSlice({
  name: 'geofences',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((g) => { state.items[g.id] = g; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Groups Slice ═══
const groupsSlice = createSlice({
  name: 'groups',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((g) => { state.items[g.id] = g; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Staff Groups Slice ═══ (org-defined groups of PEOPLE, migration 090)
const staffGroupsSlice = createSlice({
  name: 'staffGroups',
  initialState: { items: {}, loaded: false },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((g) => { state.items[g.id] = g; });
      state.loaded = true;
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
    // After a write that makes member counts / the list stale: keep the items
    // on screen and flip `loaded` so useStaffGroups refetches. A failed
    // refetch then leaves the last-known list rather than an empty one.
    invalidate: (state) => { state.loaded = false; },
    // Org switch / logout only — drops the previous tenant's data.
    reset: (state) => { state.items = {}; state.loaded = false; },
  },
});

// ═══ Drivers Slice ═══
const driversSlice = createSlice({
  name: 'drivers',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((d) => { state.items[d.id] = d; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Maintenance Slice ═══
const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((d) => { state.items[d.id] = d; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Calendars Slice ═══
const calendarsSlice = createSlice({
  name: 'calendars',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((d) => { state.items[d.id] = d; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Commands Slice ═══
const commandsSlice = createSlice({
  name: 'commands',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((item) => { state.items[item.id] = item; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ Computed Attributes Slice ═══
const computedAttributesSlice = createSlice({
  name: 'computedAttributes',
  initialState: { items: {} },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((item) => { state.items[item.id] = item; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
  },
});

// ═══ UI Slice (local UI state) ═══
const SIDEBAR_MODE_STORAGE_KEY = 'trackizy_sidebar_mode';
const readSidebarMode = () => {
  if (typeof window === 'undefined') return 'fleet';
  const v = window.localStorage.getItem(SIDEBAR_MODE_STORAGE_KEY);
  return v === 'platform' ? 'platform' : 'fleet';
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    sidebarCollapsed: typeof window !== 'undefined' && window.innerWidth <= 1024,
    sidebarMode: readSidebarMode(),   // 'fleet' | 'platform' — sultan-only switcher
    viewDensity: 'compact',       // 'compact' | 'comfortable'
    vehicleFilter: 'all',         // 'all' | 'moving' | 'stopped' | 'idle' | 'offline'
    groupFilter: null,
    companyFilter: 'all',         // enterprise Live Map: 'all' | companyId (list + markers)
    searchQuery: '',
    visibleColumns: ['status', 'icon', 'name', 'statusIcons', 'speed', 'lastUpdate'],
    commandPaletteOpen: false,
  },
  reducers: {
    toggleSidebar: (state) => { state.sidebarCollapsed = !state.sidebarCollapsed; },
    setSidebarMode: (state, action) => {
      const mode = action.payload === 'platform' ? 'platform' : 'fleet';
      state.sidebarMode = mode;
      if (typeof window !== 'undefined') window.localStorage.setItem(SIDEBAR_MODE_STORAGE_KEY, mode);
    },
    setViewDensity: (state, action) => { state.viewDensity = action.payload; },
    setVehicleFilter: (state, action) => { state.vehicleFilter = action.payload; },
    setGroupFilter: (state, action) => { state.groupFilter = action.payload; },
    setCompanyFilter: (state, action) => { state.companyFilter = action.payload || 'all'; },
    setSearchQuery: (state, action) => { state.searchQuery = action.payload; },
    setVisibleColumns: (state, action) => { state.visibleColumns = action.payload; },
    toggleCommandPalette: (state) => { state.commandPaletteOpen = !state.commandPaletteOpen; },
    setCommandPalette: (state, action) => { state.commandPaletteOpen = action.payload; },
  },
});

// ═══ Work Slice (Plan 3a — unified Work surface) ═══
const workSlice = createSlice({
  name: 'work',
  initialState: {
    mapVisibleIds: [], // Ticked works; WorkMap also draws selectedId while its detail is open.
    scopeVersion: 0, // Invalidates in-flight requests across organization switches.
    items: {},          // keyed by id (uuid)
    selectedId: null,
    kindFilter: 'all',  // 'all' | 'delivery' | 'visit' | 'inspection' | 'patrol' | 'other'
    statusFilter: 'all', // 'all' | one of the 7 work statuses
    assigneeFilter: 'all', // 'all' | userId | 'unassigned' | 'me'
    dateFilter: 'all',  // 'all' | 'today' | 'this_week' | 'overdue'
    staffGroupFilter: 'all', // 'all' | staff group id — matched on the assignee
    sortBy: 'scheduled_desc', // one of WORK_SORTS in features/work/useFilteredWork.js
    searchQuery: '',
    lastEvent: null,    // { kind, workId, stopId?, ts } — latest WS work_* event
  },
  reducers: {
    setItems: (state, action) => {
      state.items = {};
      action.payload.forEach((item) => { state.items[item.id] = item; });
    },
    toggleMapItem: (state, action) => {
      const id = action.payload;
      if (state.mapVisibleIds.includes(id)) state.mapVisibleIds = state.mapVisibleIds.filter((value) => value !== id);
      else if (state.items[id]) state.mapVisibleIds.push(id);
    },
    clearMapItems: (state) => { state.mapVisibleIds = []; },
    setLastEvent: (state, action) => {
      state.lastEvent = action.payload;
    },
    addItem: (state, action) => {
      state.items[action.payload.id] = action.payload;
    },
    updateItem: (state, action) => {
      const existing = state.items[action.payload.id];
      if (existing) {
        state.items[action.payload.id] = { ...existing, ...action.payload };
      } else {
        state.items[action.payload.id] = action.payload;
      }
    },
    removeItem: (state, action) => {
      delete state.items[action.payload];
      if (state.mapVisibleIds) state.mapVisibleIds = state.mapVisibleIds.filter((id) => id !== action.payload);
      if (state.selectedId === action.payload) state.selectedId = null;
    },
    selectItem: (state, action) => { state.selectedId = action.payload; },
    setKindFilter: (state, action) => { state.kindFilter = action.payload; },
    setStatusFilter: (state, action) => { state.statusFilter = action.payload; },
    setAssigneeFilter: (state, action) => { state.assigneeFilter = action.payload; },
    setDateFilter: (state, action) => { state.dateFilter = action.payload; },
    setStaffGroupFilter: (state, action) => { state.staffGroupFilter = action.payload; },
    setSortBy: (state, action) => { state.sortBy = action.payload; },
    setSearchQuery: (state, action) => { state.searchQuery = action.payload; },
  },
});

// ═══ Scheduled Reports Slice (Supabase-backed) ═══
const scheduledReportsSlice = createSlice({
  name: 'scheduledReports',
  initialState: {
    items: {},           // keyed by id (uuid)
    selectedId: null,
  },
  reducers: {
    setSchedules: (state, action) => {
      state.items = {};
      action.payload.forEach((s) => { state.items[s.id] = s; });
    },
    addSchedule: (state, action) => {
      state.items[action.payload.id] = action.payload;
    },
    updateSchedule: (state, action) => {
      const existing = state.items[action.payload.id];
      if (existing) {
        state.items[action.payload.id] = { ...existing, ...action.payload };
      } else {
        state.items[action.payload.id] = action.payload;
      }
    },
    removeSchedule: (state, action) => {
      delete state.items[action.payload];
      if (state.selectedId === action.payload) state.selectedId = null;
    },
    selectSchedule: (state, action) => { state.selectedId = action.payload; },
  },
});

// ═══ Org Slice (Supabase org context) ═══
const orgSlice = createSlice({
  name: 'org',
  initialState: {
    id: null,           // organization UUID
    name: null,         // organization name
    plan: null,         // 'basic' | 'pro' | 'enterprise'
    role: null,         // 'admin' | 'manager' | 'viewer'
    advancedRules: false,
    settings: {},
    loading: false,     // true while get-org-token is in flight
    token: null,        // Supabase JWT string
    enterprise: false,  // true for the synthetic enterprise org (id 'enterprise:<id>')
  },
  reducers: {
    setOrg: (state, action) => {
      const { org, role, token } = action.payload;
      state.id = org?.id ?? null;
      state.name = org?.name ?? null;
      state.plan = org?.plan ?? null;
      state.role = role ?? null;
      state.advancedRules = org?.advanced_rules ?? false;
      state.settings = org?.settings ?? {};
      state.token = token;
      state.loading = false;
      state.enterprise = org?.enterprise === true;
    },
    clearOrg: (state) => {
      state.id = null;
      state.name = null;
      state.plan = null;
      state.role = null;
      state.advancedRules = false;
      state.settings = {};
      state.token = null;
      state.loading = false;
      state.enterprise = false;
    },
    updateOrgSettings: (state, action) => { state.settings = action.payload; },
    setOrgLoading: (state, action) => { state.loading = action.payload; },
  },
});

// ═══ Rules Slice (Supabase-backed notification rules) ═══
const rulesSlice = createSlice({
  name: 'rules',
  initialState: {
    items: {},           // keyed by rule UUID
    loading: false,
  },
  reducers: {
    setRules: (state, action) => {
      state.items = {};
      action.payload.forEach((r) => { state.items[r.id] = r; });
    },
    addRule: (state, action) => {
      state.items[action.payload.id] = action.payload;
    },
    updateRule: (state, action) => {
      const existing = state.items[action.payload.id];
      state.items[action.payload.id] = existing
        ? { ...existing, ...action.payload }
        : action.payload;
    },
    removeRule: (state, action) => {
      delete state.items[action.payload];
    },
    setLoading: (state, action) => { state.loading = action.payload; },
  },
});

// ═══ POIs Slice (Supabase-backed) ═══
const poisSlice = createSlice({
  name: 'pois',
  initialState: {
    items: {},        // keyed by POI UUID
    categories: {},   // keyed by category UUID
  },
  reducers: {
    refresh: (state, action) => {
      state.items = {};
      action.payload.forEach((p) => { state.items[p.id] = p; });
    },
    update: (state, action) => { state.items[action.payload.id] = action.payload; },
    remove: (state, action) => { delete state.items[action.payload]; },
    refreshCategories: (state, action) => {
      state.categories = {};
      action.payload.forEach((c) => { state.categories[c.id] = c; });
    },
    updateCategory: (state, action) => { state.categories[action.payload.id] = action.payload; },
    removeCategory: (state, action) => { delete state.categories[action.payload]; },
  },
});

// ═══ Sultan Slice (platform superadmin state) ═══
const sultanSlice = createSlice({
  name: 'sultan',
  initialState: {
    isSultan: false,
    impersonatingOrgId: null,   // UUID of impersonated org, null when not impersonating
    impersonatingOrgName: null, // Name for banner display
    originalOrg: null,          // { id, name, plan, role, token } — snapshot before impersonation
  },
  reducers: {
    setSultan: (state, action) => {
      state.isSultan = action.payload;
    },
    clearSultan: (state) => {
      state.isSultan = false;
      state.impersonatingOrgId = null;
      state.impersonatingOrgName = null;
      state.originalOrg = null;
    },
    startImpersonation: (state, action) => {
      const { orgId, orgName, originalOrg } = action.payload;
      state.impersonatingOrgId = orgId;
      state.impersonatingOrgName = orgName;
      state.originalOrg = originalOrg;
    },
    exitImpersonation: (state) => {
      state.impersonatingOrgId = null;
      state.impersonatingOrgName = null;
      state.originalOrg = null;
    },
  },
});

// ═══ Combine & Export ═══
const combinedReducer = combineReducers({
  session: sessionSlice.reducer,
  devices: devicesSlice.reducer,
  vehicles: vehiclesSlice.reducer,
  events: eventsSlice.reducer,
  notifications: notificationsSlice.reducer,
  geofences: geofencesSlice.reducer,
  groups: groupsSlice.reducer,
  staffGroups: staffGroupsSlice.reducer,
  drivers: driversSlice.reducer,
  maintenance: maintenanceSlice.reducer,
  calendars: calendarsSlice.reducer,
  commands: commandsSlice.reducer,
  computedAttributes: computedAttributesSlice.reducer,
  ui: uiSlice.reducer,
  work: workSlice.reducer,
  scheduledReports: scheduledReportsSlice.reducer,
  org: orgSlice.reducer,
  rules: rulesSlice.reducer,
  pois: poisSlice.reducer,
  sultan: sultanSlice.reducer,
  staff: staffSlice.reducer,
  riskmize: riskmizeReducer,
});

export const sessionActions = sessionSlice.actions;
export const devicesActions = devicesSlice.actions;
export const vehiclesActions = vehiclesSlice.actions;
export const staffActions = staffSlice.actions;
export const eventsActions = eventsSlice.actions;
export const notificationsActions = notificationsSlice.actions;
export const geofencesActions = geofencesSlice.actions;
export const groupsActions = groupsSlice.actions;
export const staffGroupsActions = staffGroupsSlice.actions;
export const driversActions = driversSlice.actions;
export const maintenanceActions = maintenanceSlice.actions;
export const calendarsActions = calendarsSlice.actions;
export const commandsActions = commandsSlice.actions;
export const computedAttributesActions = computedAttributesSlice.actions;
export const uiActions = uiSlice.actions;
export const workActions = workSlice.actions;
export const scheduledReportsActions = scheduledReportsSlice.actions;
export const orgActions = orgSlice.actions;
export const rulesActions = rulesSlice.actions;
export const poisActions = poisSlice.actions;
export const sultanActions = sultanSlice.actions;
export { riskmizeActions };

/**
 * Stamp a tenant write with the scope generation captured when its fetch or
 * connection STARTED (`store.getState().session.scopeGen`). The root reducer
 * drops it if the user switched scope (enterScope) in the meantime.
 */
export function withScope(action, gen) {
  return { ...action, meta: { ...(action.meta || {}), scopeGen: gen } };
}

// Tenant slices emptied by enterScope (session.positions is cleared in its own
// reducer). Selections live inside devices/staff and go with them.
const TENANT_SLICES = {
  devices: devicesSlice,
  vehicles: vehiclesSlice,
  geofences: geofencesSlice,
  maintenance: maintenanceSlice,
  groups: groupsSlice,
  drivers: driversSlice,
  calendars: calendarsSlice,
  commands: commandsSlice,
  computedAttributes: computedAttributesSlice,
  staff: staffSlice,
  staffGroups: staffGroupsSlice,
  pois: poisSlice,
  rules: rulesSlice,
  events: eventsSlice,
  notifications: notificationsSlice,
  scheduledReports: scheduledReportsSlice,
};

export function reducer(state, action) {
  if (action.meta?.workScopeVersion !== undefined && action.meta.workScopeVersion !== state?.work.scopeVersion) return state;
  if (action.meta?.scopeGen !== undefined && state && action.meta.scopeGen !== state.session.scopeGen) return state;
  const next = combinedReducer(state, action);
  if (state && action.type === sessionSlice.actions.enterScope.type) {
    const reset = Object.fromEntries(Object.entries(TENANT_SLICES).map(([k, slice]) => [k, slice.getInitialState()]));
    return {
      ...next,
      ...reset,
      ui: { ...next.ui, groupFilter: null, companyFilter: 'all' },
      work: { ...workSlice.getInitialState(), scopeVersion: state.work.scopeVersion + 1 },
    };
  }
  if (state && (state.org.id !== next.org.id || action.type === 'session/clearUser')) {
    return {
      ...next,
      work: { ...workSlice.getInitialState(), scopeVersion: state.work.scopeVersion + 1 },
      // staffGroups latches `loaded` and is never refetched by socket.js's init,
      // so it must be cleared here — the socket org-switch reset does not run
      // across logout → login (SocketController remounts with a null ref).
      staffGroups: staffGroupsSlice.getInitialState(),
    };
  }
  return next;
}

export default configureStore({ reducer });
